import {
  ChevronDown,
  Mail,
  MailX,
  MessageSquare,
  MessageSquareOff,
  MessageSquareX,
  type LucideIcon,
} from "lucide-react";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/core/components/ui/popover";
import { Stack } from "@/core/components/ui/stack";
import { Typography } from "@/core/components/ui/typography";
import { cn } from "@/core/utils/utils";
import {
  isPayrollStatementDeliveryFailed,
  isPayrollStatementDeliverySuccessful,
  isTextOnlyDelivery,
} from "./delivery-status-rules";
import type {
  PayrollStatementNotificationDelivery,
  PayrollStatementSmsDelivery,
} from "./types";
import {
  formatTextNumber,
  getDeliveryStatusLabel,
  getNotificationReasonLabel,
  getSmsDeliveryReason,
  getSmsDeliveryStatusLabel,
} from "./utils";

type ChannelTone = "success" | "failure" | "neutral";

interface Channel {
  key: "email" | "text";
  name: string;
  Icon: LucideIcon;
  tone: ChannelTone;
  status: string;
  destination: string | null;
  detail: string | null;
}

const TONE_CLASS: Record<ChannelTone, string> = {
  success: "text-green-60",
  failure: "text-red-50",
  neutral: "text-tertiary-text",
};

const emailChannel = (
  delivery: PayrollStatementNotificationDelivery,
): Channel => {
  const failed = isPayrollStatementDeliveryFailed(delivery.status);
  return {
    key: "email",
    name: "Email",
    Icon: failed ? MailX : Mail,
    tone: failed
      ? "failure"
      : isPayrollStatementDeliverySuccessful(delivery.status)
        ? "success"
        : "neutral",
    status: getDeliveryStatusLabel(delivery.status),
    destination: delivery.recipient_email ?? null,
    detail: delivery.error
      ? (getNotificationReasonLabel(delivery.error) ?? delivery.error)
      : null,
  };
};

const textChannel = (sms: PayrollStatementSmsDelivery): Channel => ({
  key: "text",
  name: "Text",
  Icon:
    sms.status === "failed"
      ? MessageSquareX
      : sms.status === "skipped"
        ? MessageSquareOff
        : MessageSquare,
  tone:
    sms.status === "failed"
      ? "failure"
      : sms.status === "succeeded"
        ? "success"
        : "neutral",
  status: getSmsDeliveryStatusLabel(sms),
  destination: sms.to ? formatTextNumber(sms.to) : null,
  detail: getSmsDeliveryReason(sms),
});

/**
 * Every way this PayPack went out, email first. A text-only send has no email to
 * show, so it lists the text alone.
 */
export const getDeliveryChannels = (
  delivery: PayrollStatementNotificationDelivery,
): Channel[] => {
  const email = isTextOnlyDelivery(delivery) ? [] : [emailChannel(delivery)];
  return delivery.sms ? [...email, textChannel(delivery.sms)] : email;
};

/**
 * How a PayPack went out, at a glance: one icon per channel, coloured by how that
 * channel did, so a failed text stands out beside a sent email without a line of
 * its own. Opens to the per-channel detail.
 *
 * Stops click propagation: the row opens the technician's sidebar, and opening
 * this should not.
 */
export const DeliveryChannels = ({
  delivery,
}: {
  delivery: PayrollStatementNotificationDelivery;
}) => {
  const channels = getDeliveryChannels(delivery);
  const summary = channels
    .map((channel) => `${channel.name}: ${channel.status}`)
    .join(", ");

  return (
    <Popover>
      <PopoverTrigger
        aria-label={`How it was sent. ${summary}`}
        title={summary}
        onClick={(event) => event.stopPropagation()}
        className="inline-flex items-center gap-1 rounded-md px-1 py-0.5 hover:bg-surface-secondary focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-1 focus-visible:outline-stroke-secondary"
      >
        {channels.map(({ key, Icon, tone }) => (
          <Icon
            key={key}
            aria-hidden
            className={cn("h-3.5 w-3.5", TONE_CLASS[tone])}
          />
        ))}
        <ChevronDown aria-hidden className="h-3 w-3 text-tertiary-text" />
      </PopoverTrigger>
      <PopoverContent
        align="start"
        className="w-72 p-3"
        onClick={(event) => event.stopPropagation()}
      >
        <Stack gap="md">
          {channels.map(
            ({ key, name, Icon, tone, status, destination, detail }) => (
              <Stack key={key} direction="row" gap="sm" align="start" fullWidth>
                <Icon
                  aria-hidden
                  className={cn("mt-0.5 h-4 w-4 shrink-0", TONE_CLASS[tone])}
                />
                <Stack gap="xs" className="min-w-0 flex-1">
                  <Stack direction="row" justify="between" gap="sm" fullWidth>
                    <Typography variant="subtitleW3">{name}</Typography>
                    <Typography
                      variant="captionW1"
                      textColor={tone === "failure" ? "negative" : "secondary"}
                    >
                      {status}
                    </Typography>
                  </Stack>
                  {destination && (
                    <Typography
                      variant="captionW1"
                      textColor="tertiary"
                      className="truncate"
                    >
                      {destination}
                    </Typography>
                  )}
                  {detail && (
                    <Typography
                      variant="captionW1"
                      textColor={tone === "failure" ? "negative" : "tertiary"}
                      className="break-words"
                    >
                      {detail}
                    </Typography>
                  )}
                </Stack>
              </Stack>
            ),
          )}
        </Stack>
      </PopoverContent>
    </Popover>
  );
};
